import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
import React from "react";

import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { onPreferencesTabReturn } from "../utils/preferencesDelegation.js";
import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";
import useUserUiPreferenceStorageValue from "./useUserUiPreferenceStorageValue.js";

// Menu Affichage du Gantt du Dispatch : choix enregistrés pour l'utilisateur connecté. Un seul état pour toute l'application :
// les avis Geotab en direct, sur toutes les pages, lisent les mêmes choix que le menu.
export const DEFAULT_DISPATCH_DISPLAY = Object.freeze({
  product_tags: true, geotab_progress: false, geotab_durations: false,
  alert_arrival: false, alert_departure: false, alert_deliveries: true, alert_loadings: true, alert_offplan: true,
  alert_driver_rules: [], alert_others_off: false,
  alert_sound: false, alert_seconds: 20, alert_desktop: false,
});
const ENDPOINT = "/api/me/ui-preferences/dispatch-display";

let current = DEFAULT_DISPATCH_DISPLAY;
let saved = DEFAULT_DISPATCH_DISPLAY;
let loading = null;
let loaded = false;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => current;
const PRODUCT_TAGS_SESSION_PREFIX = "grain.dispatch-product-tags:";

// Appelé à la fin de la connexion, même si l'utilisateur a quitté le Dispatch.
export function resetDispatchProductTagsSession() {
  try {
    for (let index = window.sessionStorage.length - 1; index >= 0; index--) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PRODUCT_TAGS_SESSION_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch { /* Le choix en mémoire disparaît avec la vue lors de la déconnexion. */ }
}

function readSessionProductTags(key) {
  try {
    const value = userUiPreferenceStorage("session")?.getItem(key);
    return value === "true" ? true : value === "false" ? false : null;
  } catch { return null; }
}

// Le choix reste valable tant que l'utilisateur est connecté, y compris après rechargement de la page.
export function useDispatchProductTags(defaultVisible, userId) {
  const key = `${PRODUCT_TAGS_SESSION_PREFIX}${userId || "local"}`;
  const storedTags = useUserUiPreferenceStorageValue("session", key);
  const [choice, setChoice] = React.useState(() => ({ key, value: readSessionProductTags(key) }));
  React.useEffect(() => { setChoice({ key, value: readSessionProductTags(key) }); }, [key, storedTags]);
  const value = choice.key === key ? choice.value : readSessionProductTags(key);
  const onChange = React.useCallback((visible) => {
    try { userUiPreferenceStorage("session")?.setItem(key, String(visible)); } catch { /* Le choix reste en mémoire si le stockage est indisponible. */ }
    setChoice({ key, value: visible });
  }, [key]);
  return [value ?? defaultVisible, onChange];
}

function load() {
  if (loaded || loading) return;
  loading = fetchWithSupabaseAuth(ENDPOINT)
    .then((response) => (response.ok ? response.json() : null))
    .then((value) => {
      loaded = true;
      if (!value) return;
      current = saved = { ...DEFAULT_DISPATCH_DISPLAY, ...value };
      emit();
    })
    .catch(() => {})
    .finally(() => { loading = null; });
}

// Un Admin peut avoir réglé ce menu pour l'utilisateur : relu au retour sur l'onglet, sauf pendant un enregistrement.
onPreferencesTabReturn(() => {
  if (!loaded || current !== saved) return;
  fetchWithSupabaseAuth(ENDPOINT)
    .then((response) => (response.ok ? response.json() : null))
    .then((value) => {
      const next = value && { ...DEFAULT_DISPATCH_DISPLAY, ...value };
      if (!next || current !== saved || JSON.stringify(next) === JSON.stringify(saved)) return;
      current = saved = next;
      emit();
    })
    .catch(() => {});
});

subscribeUserUiPreferences("dispatch_display", (value) => {
  if (!loaded || current !== saved) return false;
  const next = { ...DEFAULT_DISPATCH_DISPLAY, ...(value || {}) };
  if (JSON.stringify(next) !== JSON.stringify(saved)) { current = saved = next; emit(); }
  return true;
});

function change(key, value, onSaveError) {
  const next = { ...current, [key]: value };
  current = next;
  emit();
  fetchWithSupabaseAuth(ENDPOINT, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(next) })
    .then((response) => {
      if (!response.ok) throw new Error("Affichage non enregistré.");
      saved = next;
    })
    .catch(() => {
      if (current !== next) return;
      current = saved;
      emit();
      onSaveError?.();
    });
}

// enabled : lu seulement quand l'utilisateur peut s'en servir (droits connus).
export default function useDispatchDisplayPreferences({ onSaveError = null, enabled = true } = {}) {
  const display = React.useSyncExternalStore(subscribe, snapshot);
  React.useEffect(() => { if (enabled) load(); }, [enabled]);
  const onChange = React.useCallback((key, value) => change(key, value, onSaveError), [onSaveError]);
  return [display, onChange];
}
